import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Wind, Activity, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { useFacialScan } from '../../contexts/FacialScanContext';

export default function ScanInProgressPage() {
  const navigate = useNavigate();
  const { 
    countdown, setCountdown, scanState, setScanState, SCAN_STATE,
    progress, updateProgress, scanDuration, waveformData,
    completeScan, failScan, generateMockResults
  } = useFacialScan();
  
  const [phase, setPhase] = useState('countdown'); // countdown, scanning, processing
  const [currentMetric, setCurrentMetric] = useState(0);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const metrics = [
    { key: 'heart_rate', icon: Heart, label: 'Heart Rate', color: '#D54B48', status: 'pending' },
    { key: 'breathing_rate', icon: Wind, label: 'Breathing Rate', color: '#8BB89F', status: 'pending' },
    { key: 'hrv_sdnn', icon: Activity, label: 'HRV', color: '#F8B64C', status: 'pending' },
    { key: 'stress_index', icon: Sparkles, label: 'Stress Index', color: '#C10891', status: 'pending' }
  ];

  const [metricStatuses, setMetricStatuses] = useState(
    metrics.map(m => ({ ...m, status: 'pending' }))
  );

  // Countdown phase
  useEffect(() => {
    if (phase === 'countdown' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (phase === 'countdown' && countdown === 0) {
      setPhase('scanning');
    }
  }, [countdown, phase, setCountdown]);

  // Scanning phase - progress update
  useEffect(() => {
    if (phase === 'scanning') {
      const startTime = Date.now();
      const duration = scanDuration * 1000;

      const updateLoop = () => {
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min((elapsed / duration) * 100, 100);
        updateProgress(newProgress);

        // Update metric statuses based on progress
        const metricProgress = Math.floor((newProgress / 100) * 4);
        setMetricStatuses(prev => prev.map((m, i) => ({
          ...m,
          status: i < metricProgress ? 'complete' : i === metricProgress ? 'scanning' : 'pending'
        })));

        if (newProgress < 100) {
          animationRef.current = requestAnimationFrame(updateLoop);
        } else {
          setPhase('processing');
        }
      };

      animationRef.current = requestAnimationFrame(updateLoop);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [phase, scanDuration, updateProgress]);

  // Processing phase
  useEffect(() => {
    if (phase === 'processing') {
      // Mark all metrics as complete
      setMetricStatuses(prev => prev.map(m => ({ ...m, status: 'complete' })));
      
      // Simulate processing delay
      const timer = setTimeout(() => {
        const results = generateMockResults();
        if (results.success) {
          completeScan(results);
          navigate('/scan/complete');
        } else {
          failScan(results.reason);
          navigate('/scan/error');
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [phase, generateMockResults, completeScan, failScan, navigate]);

  // Draw waveform
  useEffect(() => {
    if (canvasRef.current && phase === 'scanning') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      if (waveformData.length > 1) {
        ctx.beginPath();
        ctx.strokeStyle = '#C10891';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const stepX = width / (waveformData.length - 1);
        
        waveformData.forEach((point, i) => {
          const x = i * stepX;
          const y = height - (point / 100) * height;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        });

        ctx.stroke();

        // Add glow effect
        ctx.shadowColor = '#C10891';
        ctx.shadowBlur = 10;
        ctx.stroke();
      }
    }
  }, [waveformData, phase]);

  const handleCancel = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          className="rounded-full bg-black/30 text-white hover:bg-black/50"
          data-testid="cancel-scan-btn"
        >
          <X size={24} />
        </Button>
        <span className="text-white/60 text-sm">
          {phase === 'countdown' ? 'Get ready...' : phase === 'processing' ? 'Processing...' : 'Scanning...'}
        </span>
        <div className="w-10" />
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <AnimatePresence mode="wait">
          {/* Countdown */}
          {phase === 'countdown' && (
            <motion.div
              key="countdown"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.2 }}
              className="text-center"
            >
              <motion.div
                key={countdown}
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="text-9xl font-bold text-primary mb-4"
              >
                {countdown}
              </motion.div>
              <p className="text-white/60 text-lg">Hold still...</p>
            </motion.div>
          )}

          {/* Scanning */}
          {phase === 'scanning' && (
            <motion.div
              key="scanning"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full max-w-sm"
            >
              {/* Face silhouette with pulse */}
              <div className="relative w-40 h-48 mx-auto mb-8">
                <motion.div
                  animate={{ 
                    boxShadow: [
                      '0 0 20px rgba(193, 8, 145, 0.3)',
                      '0 0 40px rgba(193, 8, 145, 0.5)',
                      '0 0 20px rgba(193, 8, 145, 0.3)'
                    ]
                  }}
                  transition={{ duration: 1, repeat: Infinity }}
                  className="absolute inset-0 rounded-[50%] border-2 border-primary/50"
                />
                <div className="absolute inset-4 rounded-[50%] bg-gradient-to-b from-primary/20 to-transparent" />
              </div>

              {/* Waveform */}
              <div className="relative h-20 mb-6 bg-black/30 rounded-xl overflow-hidden">
                <canvas 
                  ref={canvasRef} 
                  width={300} 
                  height={80}
                  className="w-full h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black" />
              </div>

              {/* Progress */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-white/60">Analyzing...</span>
                  <span className="text-white font-medium">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Metrics being captured */}
              <div className="grid grid-cols-2 gap-3">
                {metricStatuses.map((metric, index) => {
                  const Icon = metric.icon;
                  return (
                    <motion.div
                      key={metric.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        metric.status === 'complete' 
                          ? 'bg-white/10' 
                          : metric.status === 'scanning'
                          ? 'bg-primary/20'
                          : 'bg-white/5'
                      }`}
                    >
                      <div 
                        className={`p-2 rounded-lg transition-colors ${
                          metric.status === 'pending' ? 'bg-white/10' : ''
                        }`}
                        style={{ backgroundColor: metric.status !== 'pending' ? `${metric.color}30` : undefined }}
                      >
                        {metric.status === 'scanning' ? (
                          <Loader2 size={16} className="animate-spin" style={{ color: metric.color }} />
                        ) : (
                          <Icon 
                            size={16} 
                            style={{ color: metric.status === 'pending' ? '#666' : metric.color }} 
                          />
                        )}
                      </div>
                      <span className={`text-sm ${
                        metric.status === 'pending' ? 'text-white/40' : 'text-white'
                      }`}>
                        {metric.label}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Processing */}
          {phase === 'processing' && (
            <motion.div
              key="processing"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="w-20 h-20 mx-auto mb-6"
              >
                <div className="w-full h-full rounded-full border-4 border-primary/20 border-t-primary" />
              </motion.div>
              <h2 className="text-white text-xl font-heading font-semibold mb-2">
                Processing Results
              </h2>
              <p className="text-white/60">Analyzing your biometrics...</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom info */}
      <div className="p-6 pb-10 text-center">
        <p className="text-white/40 text-xs">
          Stay still for accurate measurements
        </p>
      </div>
    </div>
  );
}
